import React, { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControl, FormControlLabel, Grid, IconButton, InputLabel, MenuItem, Select, Switch, TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {
  ENDODONTIC_STATUSES,
  ENDODONTIC_TREATMENT_TYPES,
  PERIAPICAL_DIAGNOSES,
  PULP_DIAGNOSES,
  getDentition,
} from '../../constants/odontology';
import type { EndodonticCanal, EndodonticSession, EndodonticTreatment } from '../../types/odontology';
import { createEndodonticTreatment, updateEndodonticTreatment } from '../../services/odontologyService';
import ToothSelect from './ToothSelect';
import { todayIso, useDentalProfessionals } from './odontologyHooks';

export interface EndodonticTreatmentDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  unitId?: string | null;
  treatment?: EndodonticTreatment | null;
  defaultToothNumber?: string | null;
  defaultProfessionalId?: string | null;
  onSaved: () => void;
}

const emptyCanal = (): EndodonticCanal => ({
  name: '',
  working_length_mm: null,
  reference: '',
  initial_file: '',
  master_apical_file: '',
  patency: true,
});

const EndodonticTreatmentDialog: React.FC<EndodonticTreatmentDialogProps> = ({
  open,
  onClose,
  patientId,
  unitId,
  treatment,
  defaultToothNumber,
  defaultProfessionalId,
  onSaved,
}) => {
  const [toothNumber, setToothNumber] = useState('');
  const [treatmentType, setTreatmentType] = useState('conventional');
  const [pulpDiagnosis, setPulpDiagnosis] = useState('');
  const [periapicalDiagnosis, setPeriapicalDiagnosis] = useState('');
  const [canals, setCanals] = useState<EndodonticCanal[]>([]);
  const [instrumentation, setInstrumentation] = useState('');
  const [irrigation, setIrrigation] = useState('');
  const [medication, setMedication] = useState('');
  const [obturation, setObturation] = useState('');
  const [sealer, setSealer] = useState('');
  const [rubberDam, setRubberDam] = useState(true);
  const [sessions, setSessions] = useState<EndodonticSession[]>([]);
  const [status, setStatus] = useState('planned');
  const [professionalId, setProfessionalId] = useState('');
  const [startedDate, setStartedDate] = useState('');
  const [completedDate, setCompletedDate] = useState('');
  const [followupDate, setFollowupDate] = useState('');
  const [notes, setNotes] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const professionals = useDentalProfessionals(open);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (treatment) {
      setToothNumber(treatment.tooth_number || '');
      setTreatmentType(treatment.treatment_type || 'conventional');
      setPulpDiagnosis(treatment.pulp_diagnosis || '');
      setPeriapicalDiagnosis(treatment.periapical_diagnosis || '');
      setCanals(treatment.canals?.length ? treatment.canals : []);
      setInstrumentation(treatment.instrumentation_technique || '');
      setIrrigation(treatment.irrigation_solution || '');
      setMedication(treatment.intracanal_medication || '');
      setObturation(treatment.obturation_technique || '');
      setSealer(treatment.sealer || '');
      setRubberDam(treatment.rubber_dam !== false);
      setSessions(treatment.sessions || []);
      setStatus(treatment.status || 'planned');
      setProfessionalId(treatment.professional_id || '');
      setStartedDate(treatment.started_date?.slice(0, 10) || '');
      setCompletedDate(treatment.completed_date?.slice(0, 10) || '');
      setFollowupDate(treatment.followup_date?.slice(0, 10) || '');
      setNotes(treatment.notes || '');
    } else {
      setToothNumber(defaultToothNumber || '');
      setTreatmentType('conventional');
      setPulpDiagnosis('');
      setPeriapicalDiagnosis('');
      setCanals([emptyCanal()]);
      setInstrumentation('');
      setIrrigation('Hipoclorito de sódio 2,5%');
      setMedication('');
      setObturation('');
      setSealer('');
      setRubberDam(true);
      setSessions([]);
      setStatus('planned');
      setProfessionalId(defaultProfessionalId || '');
      setStartedDate('');
      setCompletedDate('');
      setFollowupDate('');
      setNotes('');
    }
  }, [open, treatment, defaultToothNumber, defaultProfessionalId]);

  const updateCanal = (index: number, patch: Partial<EndodonticCanal>) => {
    setCanals((prev) => prev.map((canal, i) => (i === index ? { ...canal, ...patch } : canal)));
  };

  const updateSession = (index: number, patch: Partial<EndodonticSession>) => {
    setSessions((prev) => prev.map((session, i) => (i === index ? { ...session, ...patch } : session)));
  };

  const handleSave = async () => {
    if (!toothNumber) {
      setError('Selecione o dente.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        patient_id: patientId,
        unit_id: unitId || undefined,
        professional_id: professionalId || null,
        tooth_number: toothNumber,
        dentition: getDentition(toothNumber),
        treatment_type: treatmentType,
        pulp_diagnosis: pulpDiagnosis || null,
        periapical_diagnosis: periapicalDiagnosis || null,
        canals: canals.filter((c) => c.name.trim()),
        instrumentation_technique: instrumentation.trim() || null,
        irrigation_solution: irrigation.trim() || null,
        intracanal_medication: medication.trim() || null,
        obturation_technique: obturation.trim() || null,
        sealer: sealer.trim() || null,
        rubber_dam: rubberDam,
        sessions: sessions.filter((s) => s.date && s.procedure.trim()),
        status,
        started_date: startedDate || null,
        completed_date: completedDate || null,
        followup_date: followupDate || null,
        notes: notes.trim() || null,
      };

      if (treatment?.id) await updateEndodonticTreatment(treatment.id, payload);
      else await createEndodonticTreatment(payload);

      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Erro ao salvar tratamento endodôntico');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography component="span" sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#0F172A' }}>
          {treatment ? 'Editar tratamento endodôntico' : 'Novo tratamento endodôntico'}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <ToothSelect value={toothNumber} onChange={setToothNumber} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="endo-type-label">Tipo de tratamento</InputLabel>
              <Select
                labelId="endo-type-label"
                label="Tipo de tratamento"
                value={treatmentType}
                onChange={(e) => setTreatmentType(e.target.value)}
              >
                {ENDODONTIC_TREATMENT_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              freeSolo
              size="small"
              options={PULP_DIAGNOSES}
              value={pulpDiagnosis}
              onInputChange={(_, value) => setPulpDiagnosis(value)}
              renderInput={(params) => <TextField {...params} label="Diagnóstico pulpar" />}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              freeSolo
              size="small"
              options={PERIAPICAL_DIAGNOSES}
              value={periapicalDiagnosis}
              onInputChange={(_, value) => setPeriapicalDiagnosis(value)}
              renderInput={(params) => <TextField {...params} label="Diagnóstico periapical" />}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider textAlign="left" sx={{ my: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Odontometria
              </Typography>
            </Divider>
          </Grid>

          {canals.map((canal, index) => (
            <Grid item xs={12} key={index}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                <TextField
                  size="small"
                  label="Canal"
                  value={canal.name}
                  onChange={(e) => updateCanal(index, { name: e.target.value })}
                  placeholder="MV"
                  sx={{ width: { xs: '100%', md: 110 } }}
                />
                <TextField
                  size="small"
                  label="CT (mm)"
                  value={canal.working_length_mm ?? ''}
                  onChange={(e) => updateCanal(index, {
                    working_length_mm: e.target.value ? Number(e.target.value.replace(',', '.')) : null,
                  })}
                  placeholder="21,5"
                  sx={{ width: { xs: '48%', md: 110 } }}
                />
                <TextField
                  size="small"
                  label="Referência"
                  value={canal.reference ?? ''}
                  onChange={(e) => updateCanal(index, { reference: e.target.value })}
                  placeholder="Cúspide MV"
                  sx={{ flex: 1, minWidth: { xs: '48%', md: 140 } }}
                />
                <TextField
                  size="small"
                  label="Lima inicial"
                  value={canal.initial_file ?? ''}
                  onChange={(e) => updateCanal(index, { initial_file: e.target.value })}
                  placeholder="#15"
                  sx={{ width: { xs: '48%', md: 120 } }}
                />
                <TextField
                  size="small"
                  label="LAP"
                  value={canal.master_apical_file ?? ''}
                  onChange={(e) => updateCanal(index, { master_apical_file: e.target.value })}
                  placeholder="#35"
                  sx={{ width: { xs: '48%', md: 100 } }}
                />
                <IconButton
                  size="small"
                  onClick={() => setCanals((prev) => prev.filter((_, i) => i !== index))}
                  sx={{ color: '#94A3B8', mt: 0.5 }}
                  aria-label="Remover canal"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Grid>
          ))}

          <Grid item xs={12}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setCanals((prev) => [...prev, emptyCanal()])}
              sx={{ textTransform: 'none', color: '#0D9488' }}
            >
              Adicionar canal
            </Button>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Técnica de instrumentação" value={instrumentation}
              onChange={(e) => setInstrumentation(e.target.value)} placeholder="Ex.: rotatória ProTaper Gold" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Solução irrigadora" value={irrigation}
              onChange={(e) => setIrrigation(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Medicação intracanal" value={medication}
              onChange={(e) => setMedication(e.target.value)} placeholder="Ex.: hidróxido de cálcio" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Técnica de obturação" value={obturation}
              onChange={(e) => setObturation(e.target.value)} placeholder="Ex.: condensação lateral" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Cimento endodôntico" value={sealer}
              onChange={(e) => setSealer(e.target.value)} placeholder="Ex.: AH Plus" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Switch checked={rubberDam} onChange={(e) => setRubberDam(e.target.checked)} />}
              label="Isolamento absoluto"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider textAlign="left" sx={{ my: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Sessões
              </Typography>
            </Divider>
          </Grid>

          {sessions.map((session, index) => (
            <Grid item xs={12} key={index}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                <TextField
                  size="small"
                  type="date"
                  label="Data"
                  InputLabelProps={{ shrink: true }}
                  value={session.date}
                  onChange={(e) => updateSession(index, { date: e.target.value })}
                  sx={{ width: { xs: '100%', md: 170 } }}
                />
                <TextField
                  size="small"
                  label="Procedimento"
                  value={session.procedure}
                  onChange={(e) => updateSession(index, { procedure: e.target.value })}
                  placeholder="Acesso, odontometria, obturação…"
                  sx={{ flex: 1, minWidth: { xs: '100%', md: 200 } }}
                />
                <IconButton
                  size="small"
                  onClick={() => setSessions((prev) => prev.filter((_, i) => i !== index))}
                  sx={{ color: '#94A3B8', mt: 0.5 }}
                  aria-label="Remover sessão"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Grid>
          ))}

          <Grid item xs={12}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setSessions((prev) => [...prev, { date: todayIso(), procedure: '' }])}
              sx={{ textTransform: 'none', color: '#0D9488' }}
            >
              Adicionar sessão
            </Button>
          </Grid>

          <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="endo-status-label">Status</InputLabel>
              <Select labelId="endo-status-label" label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                {ENDODONTIC_STATUSES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="endo-professional-label">Profissional</InputLabel>
              <Select
                labelId="endo-professional-label"
                label="Profissional"
                value={professionalId}
                onChange={(e) => setProfessionalId(e.target.value)}
              >
                <MenuItem value="">Não informado</MenuItem>
                {professionals.map((p) => <MenuItem key={p.id} value={p.id}>{p.full_name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" type="date" label="Início" InputLabelProps={{ shrink: true }}
              value={startedDate} onChange={(e) => setStartedDate(e.target.value)} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" type="date" label="Conclusão" InputLabelProps={{ shrink: true }}
              value={completedDate} onChange={(e) => setCompletedDate(e.target.value)} />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField fullWidth size="small" type="date" label="Proservação" InputLabelProps={{ shrink: true }}
              value={followupDate} onChange={(e) => setFollowupDate(e.target.value)} />
          </Grid>

          <Grid item xs={12}>
            <TextField fullWidth size="small" multiline minRows={2} label="Observações" value={notes}
              onChange={(e) => setNotes(e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#64748B' }}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ textTransform: 'none', bgcolor: '#0D9488', '&:hover': { bgcolor: '#0F766E' } }}
        >
          {saving ? 'Salvando…' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EndodonticTreatmentDialog;
